// g2-plan-content-snapshot.ts
//
// H4 A1 (design bản 2 §1, 2026-09-29): what a G2 approval is bound to. Before
// this the G2 request id was `G2-<mission>` with no artifact hash, so an
// approval covered whatever the plan later became (probes A/C/D in
// reports/research-260929-h4-g2-plan-approval.md).
//
// Snapshot = the plan CONTENT a person approves:
//   - every effective plan ticket (not bootstrap, not controller-reserved,
//     not cancelled/superseded, not a controller-made redirect decision —
//     D5), with every field EXCEPT progress: `revision`,
//     `acceptance[].done`, `acceptance[].evidence` (blacklist, so a field
//     added to the schema later is hashed by default — fail closed);
//     set-valued arrays sorted;
//   - the full definition of every verification recipe those tickets use,
//     plus `baseline_exceptions`, from `.avatar/verification-profile.json`
//     ("unreadable" when it cannot be read — never silently empty).
// NOT hashed: PLAN.md/phase bytes (workers tick checkboxes there — Q6),
// runtime status/attempts/verdicts, BLUEPRINT (G0's job), resource_names.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { ensureDir } from "../filesystem-helpers.js";
import { canonicalJson, sha256Checksum } from "./canonical-json-and-checksum.js";
import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import { writeFileDurable } from "./durable-file-writer.js";
import { BOOTSTRAP_TICKET_KINDS } from "./feature-dispatch-gate-guard.js";
import { RETIRED_TICKET_STATUSES } from "./g2-plan-readiness.js";
import type { MissionState } from "./mission-state-reducer.js";
import type { Ticket } from "./schemas/ticket-schema.js";

export const G2_PLAN_SNAPSHOT_VERSION = "g2-plan-v1";
const SET_VALUED_FIELDS = ["depends_on", "allowed_paths", "required_capabilities", "sources"];
const PROGRESS_ONLY_FIELDS = ["revision"];

export interface G2PlanSnapshot {
  v: string;
  tickets: Array<Record<string, unknown>>;
  /** An array, not an object keyed by id: a recipe id like `__proto__` must stay in the hash. */
  recipes: Array<{ recipe_id: string; definition: unknown }> | "unreadable";
  baseline_exceptions: unknown;
}

/** D5: decision tickets the CONTROLLER wrote from a redirect text, still in their read-only shape. */
function controllerRedirectDecisionIds(state: Pick<MissionState, "intents">): Set<string> {
  const ids = new Set<string>();
  for (const intent of Object.values(state.intents)) {
    if (intent.operation_kind !== "redirect_text_to_decision_ticket") continue;
    const id = intent.params.ticket_id;
    if (typeof id === "string") ids.add(id);
  }
  return ids;
}

function isControllerRedirectDecision(ticket: Ticket, ledger: Set<string>): boolean {
  return (
    ledger.has(ticket.id) &&
    ticket.revision === 1 && // never amended since the controller wrote it (review L1)
    ticket.kind === "decision" &&
    ticket.command_adapter === "decision-v1" &&
    ticket.allowed_paths.length === 0
  );
}

function ticketContent(ticket: Ticket): Record<string, unknown> {
  const content: Record<string, unknown> = { ...ticket };
  for (const field of PROGRESS_ONLY_FIELDS) delete content[field];
  for (const field of SET_VALUED_FIELDS) {
    const value = content[field];
    if (Array.isArray(value)) content[field] = [...value].map(String).sort();
  }
  if (Array.isArray(ticket.acceptance)) {
    // Blacklist here too (review M2): only the progress keys leave the hash.
    content.acceptance = ticket.acceptance.map((row) => {
      const { done: _done, evidence: _evidence, ...rest } = row as Record<string, unknown>;
      return rest;
    });
  }
  return content;
}

async function readProfileParts(
  repoDir: string,
  recipeIds: readonly string[],
): Promise<Pick<G2PlanSnapshot, "recipes" | "baseline_exceptions">> {
  try {
    const raw = JSON.parse(
      await fs.readFile(join(repoDir, ".avatar", "verification-profile.json"), "utf8"),
    ) as { recipes?: Array<{ recipe_id?: string }>; baseline_exceptions?: unknown };
    const recipes = [...new Set(recipeIds)].sort().map((id) => ({
      recipe_id: id,
      definition: raw.recipes?.find((r) => r.recipe_id === id) ?? null,
    }));
    return { recipes, baseline_exceptions: raw.baseline_exceptions ?? [] };
  } catch {
    return { recipes: "unreadable", baseline_exceptions: "unreadable" };
  }
}

/** D5 ticket ids — excluded from the snapshot AND from the plan-not-ready hold (review M1). */
export function controllerRedirectDecisionTicketIds(
  tickets: readonly Ticket[],
  state: Pick<MissionState, "intents">,
): string[] {
  const ledger = controllerRedirectDecisionIds(state);
  return tickets.filter((t) => isControllerRedirectDecision(t, ledger)).map((t) => t.id);
}

export async function buildG2PlanSnapshot(input: {
  repoDir: string;
  tickets: readonly Ticket[];
  ticketStatus: Record<string, string>;
  state: Pick<MissionState, "intents">;
}): Promise<G2PlanSnapshot> {
  const ledger = controllerRedirectDecisionIds(input.state);
  const planTickets = input.tickets
    .filter((t) => !BOOTSTRAP_TICKET_KINDS.includes(t.kind))
    .filter((t) => !isControllerReservedTicketId(t.id))
    .filter((t) => !RETIRED_TICKET_STATUSES.has(input.ticketStatus[t.id] ?? "todo"))
    .filter((t) => !isControllerRedirectDecision(t, ledger))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const profile = await readProfileParts(
    input.repoDir,
    planTickets.map((t) => t.verification_recipe),
  );
  return { v: G2_PLAN_SNAPSHOT_VERSION, tickets: planTickets.map(ticketContent), ...profile };
}

export function hashG2PlanSnapshot(snapshot: G2PlanSnapshot): string {
  return sha256Checksum(canonicalJson(snapshot));
}

function snapshotPath(controlRoot: string, hash: string): string {
  return join(controlRoot, "g2-plan-snapshots", `${hash}.json`);
}

/** Written durably BEFORE the G2 request, so the approver (and a later diff) can see exactly what the id stands for. */
export async function writeG2PlanSnapshot(
  controlRoot: string,
  hash: string,
  snapshot: G2PlanSnapshot,
): Promise<void> {
  await ensureDir(join(controlRoot, "g2-plan-snapshots"));
  await writeFileDurable(
    snapshotPath(controlRoot, hash),
    Buffer.from(`${JSON.stringify(snapshot, null, 2)}\n`, "utf8"),
  );
}

/** "+T-0019 ~T-0005 −T-0007" between the snapshot behind an older request/approval and the current one; "" when the old one is unknown. */
export async function describeG2PlanChange(
  controlRoot: string,
  oldHash: string | undefined,
  current: G2PlanSnapshot,
): Promise<string> {
  try {
    if (oldHash === undefined) return "";
    let old: G2PlanSnapshot;
    try {
      old = JSON.parse(await fs.readFile(snapshotPath(controlRoot, oldHash), "utf8"));
    } catch {
      return "";
    }
    const byId = (s: G2PlanSnapshot) =>
      new Map(s.tickets.map((t) => [String(t.id), canonicalJson(t)]));
    const before = byId(old);
    const after = byId(current);
    const parts = [
      ...[...after.keys()].filter((id) => !before.has(id)).map((id) => `+${id}`),
      ...[...after.keys()]
        .filter((id) => before.has(id) && before.get(id) !== after.get(id))
        .map((id) => `~${id}`),
      ...[...before.keys()].filter((id) => !after.has(id)).map((id) => `−${id}`),
    ];
    if (canonicalJson(old.recipes) !== canonicalJson(current.recipes)) parts.push("~recipes");
    if (canonicalJson(old.baseline_exceptions) !== canonicalJson(current.baseline_exceptions)) {
      parts.push("~baseline_exceptions");
    }
    return parts.join(" ");
  } catch {
    // Review L3: a snapshot of an unexpected shape must never stall a tick —
    // the diff is a courtesy in the reason text, not a decision input.
    return "";
  }
}
